import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    activateAgent, assignAgentSite, checkInAgent, checkOutAgent, inviteAgent, listAgents, listSites,
    removeAgentAuthenticator, setUpAgentAuthenticator, signOutAgentPhones, suspendAgent, unassignAgentSite,
} from "../api/admin";
import AuthenticatorSetup from "../components/AuthenticatorSetup";

const KIND = {ON_SITE: "On-site", FLOATING: "Floating", BUILDER_STAFF: "Builder staff", DESK: "Desk"};
const TONE = {ACTIVE: "green", PENDING: "amber", SUSPENDED: "grey"};
const EMPTY = {name: "", mobile: "", email: "", role: "AGENT", kind: "ON_SITE"};

const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], {hour: "numeric", minute: "2-digit"}) : "");

/**
 * Agents: add, suspend or reactivate, sign out a lost phone, choose their sites, check them in or out when their
 * phone can't get a location, and authenticator sign-in (a QR code to scan; codes then work instead of WhatsApp).
 */
const AgentsTable = ({onAuthError}) => {
    const [agents, setAgents] = useState(null);
    const [sites, setSites] = useState([]);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(null);
    const [showing, setShowing] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [filter, setFilter] = useState("ACTIVE");

    const load = useCallback(async () => {
        setError("");
        try {
            const [a, s] = await Promise.all([listAgents(), listSites()]);
            setAgents(a);
            setSites(s);
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        }
    }, [onAuthError]);

    useEffect(() => { load(); }, [load]);

    const run = async (key, fn, done) => {
        setBusy(key);
        setError("");
        setNotice("");
        try {
            const result = await fn();
            if (done) done(result);
            await load();
            return true;
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
            return false;
        } finally {
            setBusy(null);
            setConfirming(null);
        }
    };

    const add = async (e) => {
        e.preventDefault();
        const mobile = form.mobile.replace(/\D/g, "");
        if (!form.name.trim()) return setError("Please enter their name.");
        if (!/^[6-9]\d{9}$/.test(mobile)) return setError("Please enter a valid 10-digit mobile number.");
        const name = form.name.trim();
        const ok = await run("add", () => inviteAgent({...form, name, mobile, email: form.email.trim() || null}));
        if (ok) {
            setForm(EMPTY);
            setAdding(false);
            setNotice(`${name} is added. Choose their sites below; they sign in to the OGM Agent app with ${mobile}.`);
        }
    };

    const assignable = useMemo(() => sites.filter((s) => !s.builderOnly), [sites]);
    const shown = (agents || []).filter((a) => filter === "ALL" || a.status === filter);

    const confirmRow = (a, label, fn) => (
        <span className="brk-row-actions">
            <span className="brk-hint">{label}?</span>
            <button type="button" className="brk-link brk-link--danger" disabled={busy === a.agentId}
                    onClick={() => run(a.agentId, fn)}>Yes</button>
            <button type="button" className="brk-link" onClick={() => setConfirming(null)}>No</button>
        </span>
    );

    return (
        <div>
            <div className="brk-card__head">
                <div className="brk-filters" role="group" aria-label="Filter agents by status">
                    {["ACTIVE", "PENDING", "SUSPENDED", "ALL"].map((s) => (
                        <button key={s} type="button" aria-pressed={filter === s}
                                className={`brk-filter${filter === s ? " brk-filter--on" : ""}`} onClick={() => setFilter(s)}>
                            {s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
                            <span>{(agents || []).filter((a) => s === "ALL" || a.status === s).length}</span>
                        </button>
                    ))}
                </div>
                <span className="brk-row-actions">
                    {!adding && <button type="button" className="brk-btn brk-btn--primary brk-btn--sm" onClick={() => setAdding(true)}>+ Add agent</button>}
                    <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" onClick={load}>Refresh</button>
                </span>
            </div>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}
            {notice && <div className="brk-notice brk-notice--ok" role="status">{notice}</div>}
            {showing && <AuthenticatorSetup name={showing.name} setup={showing.setup} onDone={() => setShowing(null)}/>}

            {adding && (
                <form className="brk-grid" onSubmit={add} style={{marginBottom: 20}}>
                    <label className="brk-field">
                        <span className="brk-field__label">Name</span>
                        <input className="brk-input" value={form.name} maxLength={120} autoFocus
                               onChange={(e) => setForm((f) => ({...f, name: e.target.value}))}/>
                    </label>
                    <label className="brk-field">
                        <span className="brk-field__label">Mobile (they sign in with this)</span>
                        <input className="brk-input" inputMode="numeric" value={form.mobile} placeholder="10-digit mobile"
                               onChange={(e) => setForm((f) => ({...f, mobile: e.target.value.replace(/\D/g, "").slice(0, 10)}))}/>
                    </label>
                    <label className="brk-field">
                        <span className="brk-field__label">Email (optional)</span>
                        <input className="brk-input" type="email" value={form.email}
                               onChange={(e) => setForm((f) => ({...f, email: e.target.value}))}/>
                    </label>
                    <label className="brk-field">
                        <span className="brk-field__label">Works</span>
                        <select className="brk-input" value={form.kind} onChange={(e) => setForm((f) => ({...f, kind: e.target.value}))}>
                            <option value="ON_SITE">At one or a few sites</option>
                            <option value="FLOATING">Across a cluster of nearby sites</option>
                        </select>
                    </label>
                    <label className="brk-field">
                        <span className="brk-field__label">Role</span>
                        <select className="brk-input" value={form.role} onChange={(e) => setForm((f) => ({...f, role: e.target.value}))}>
                            <option value="AGENT">Agent</option>
                            <option value="AGENT_ADMIN">Agent admin (manages agents in the app)</option>
                        </select>
                    </label>
                    <div className="brk-actions brk-field--wide">
                        <button type="submit" className="brk-btn brk-btn--primary brk-btn--sm" disabled={busy === "add"}>
                            {busy === "add" ? "Adding…" : "Add agent"}
                        </button>
                        <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm"
                                onClick={() => { setAdding(false); setForm(EMPTY); setError(""); }}>Cancel</button>
                    </div>
                </form>
            )}

            {!agents ? <p className="brk-empty">Loading…</p> : shown.length === 0 ? <p className="brk-empty">No agents here.</p> : (
                <div className="brk-table-wrap">
                    <table className="brk-table">
                        <thead>
                        <tr><th>Agent</th><th>Status</th><th>Right now</th><th>Sites</th><th>Sign-in</th><th/></tr>
                        </thead>
                        <tbody>
                        {shown.map((a) => {
                            const active = a.status === "ACTIVE";
                            const here = a.checkin && !a.checkin.checkedOutAt ? a.checkin : null;
                            const assigned = new Set(a.sites.map((s) => `${s.siteType}-${s.siteId}`));
                            const addable = a.kind === "BUILDER_STAFF" ? [] : assignable.filter((s) => !assigned.has(`${s.siteType}-${s.siteId}`));
                            return (
                                <tr key={a.agentId}>
                                    <td>
                                        <div className="brk-strong">{a.name || "—"}</div>
                                        <div className="brk-hint">
                                            {a.mobile ? a.mobile.replace(/^91/, "") : "No mobile"}
                                            {" · "}{KIND[a.kind] || a.kind}{a.role === "AGENT_ADMIN" ? " · Agent admin" : ""}
                                        </div>
                                        {a.builderName && <div className="brk-hint">{a.builderName}</div>}
                                    </td>
                                    <td><span className={`brk-badge brk-badge--${TONE[a.status] || "grey"}`}>{a.status}</span></td>
                                    <td>
                                        {here ? (
                                            <>
                                                <span className="brk-badge brk-badge--green">{a.busy ? "On a tour" : "Checked in"}</span>
                                                <div className="brk-hint">{here.siteName} since {clock(here.checkedInAt)}</div>
                                                <button type="button" className="brk-link" disabled={busy === a.agentId}
                                                        onClick={() => run(a.agentId, () => checkOutAgent(a.agentId))}>Check out</button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="brk-hint">Not checked in</span>
                                                {active && a.sites.length > 0 && (
                                                    <div>
                                                        <select className="brk-input brk-input--sm" value="" disabled={busy === a.agentId}
                                                                aria-label={`Check ${a.name} in at a site`}
                                                                onChange={(e) => {
                                                                    const [type, id] = e.target.value.split(":");
                                                                    if (type) run(a.agentId, () => checkInAgent(a.agentId, type, Number(id)));
                                                                }}>
                                                            <option value="">Check in at…</option>
                                                            {a.sites.map((s) => (
                                                                <option key={`${s.siteType}-${s.siteId}`} value={`${s.siteType}:${s.siteId}`}>{s.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        <div className="brk-chips">
                                            {a.sites.map((s) => (
                                                <span key={s.assignmentId ?? `${s.siteType}-${s.siteId}`} className="brk-chip brk-chip--on">
                                                    {s.name || `${s.siteType.toLowerCase()} ${s.siteId}`}
                                                    {s.assignmentId && (
                                                        <button type="button" className="brk-link" aria-label={`Remove ${s.name} from ${a.name}`}
                                                                disabled={busy === a.agentId}
                                                                onClick={() => run(a.agentId, () => unassignAgentSite(a.agentId, s.assignmentId))}>✕</button>
                                                    )}
                                                </span>
                                            ))}
                                            {active && addable.length > 0 && (
                                                <select className="brk-input brk-input--sm" value="" disabled={busy === a.agentId}
                                                        aria-label={`Add a site for ${a.name}`}
                                                        onChange={(e) => {
                                                            const [type, id] = e.target.value.split(":");
                                                            if (type) run(a.agentId, () => assignAgentSite(a.agentId, type, Number(id)));
                                                        }}>
                                                    <option value="">+ Add site</option>
                                                    {addable.map((s) => <option key={`${s.siteType}-${s.siteId}`} value={`${s.siteType}:${s.siteId}`}>{s.name}</option>)}
                                                </select>
                                            )}
                                        </div>
                                        {active && a.sites.length === 0 && <div className="brk-hint">No sites: tours won't ring them.</div>}
                                    </td>
                                    <td>
                                        {a.hasAuthenticator ? <span className="brk-badge brk-badge--green">Authenticator</span>
                                            : <span className="brk-hint">WhatsApp code</span>}
                                        {active && a.mobile && (
                                            <div>
                                                <button type="button" className="brk-link" disabled={busy === a.agentId}
                                                        onClick={() => run(a.agentId, () => setUpAgentAuthenticator(a.agentId),
                                                            (setup) => setShowing({name: a.name, setup}))}>
                                                    {a.hasAuthenticator ? "New QR code" : "Set up authenticator"}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {confirming?.id === a.agentId ? confirmRow(a, confirming.label, confirming.fn) : (
                                            <span className="brk-row-actions" style={{flexDirection: "column", alignItems: "flex-start"}}>
                                                {active ? (
                                                    <>
                                                        <button type="button" className="brk-link"
                                                                onClick={() => setConfirming({id: a.agentId, label: "Sign out all their phones",
                                                                    fn: () => signOutAgentPhones(a.agentId)})}>Sign out phones</button>
                                                        {a.hasAuthenticator && (
                                                            <button type="button" className="brk-link brk-link--danger"
                                                                    onClick={() => setConfirming({id: a.agentId, label: "Turn off authenticator",
                                                                        fn: () => removeAgentAuthenticator(a.agentId)})}>Turn off authenticator</button>
                                                        )}
                                                        <button type="button" className="brk-link brk-link--danger"
                                                                onClick={() => setConfirming({id: a.agentId, label: `Suspend ${a.name}`,
                                                                    fn: () => suspendAgent(a.agentId)})}>Suspend</button>
                                                    </>
                                                ) : (
                                                    <button type="button" className="brk-link" disabled={busy === a.agentId}
                                                            onClick={() => run(a.agentId, () => activateAgent(a.agentId))}>
                                                        {a.status === "PENDING" ? "Approve" : "Reactivate"}
                                                    </button>
                                                )}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AgentsTable;
